import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import Lenis from "lenis";
import { Radiation } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { LaserCanvas } from "@/components/LaserCanvas";
import { RatLayer } from "@/components/RatLayer";
import { FruitParty } from "@/components/FruitParty";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Manifesto } from "@/components/Manifesto";
import { Lineup } from "@/components/Lineup";
import { LocationReveal } from "@/components/LocationReveal";
import { RsvpForm } from "@/components/RsvpForm";
import { MusicPlayer } from "@/components/MusicPlayer";
import { IntroOverlay } from "@/components/IntroOverlay";
import GuestsPage from "@/components/GuestsPage";
import { CouncilGate } from "@/components/CouncilGate";
import { startAmbience, playSparkle } from "@/lib/audio";
import { isAlly, setAlly } from "@/lib/theme";

const PRIDE = ["#FF0000", "#FF9900", "#FFEE00", "#33CC33", "#3399FF", "#CC66FF", "#FF71CE", "#01CDFE"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Header = ({ ally, onToggle }) => (
  <header className="fixed top-0 inset-x-0 z-50 flex items-center justify-between px-6 sm:px-12 py-4 bg-[#08080C]/70 backdrop-blur-xl border-b border-white/10" data-testid="site-header">
    <div className="flex items-center gap-2">
      <Radiation size={18} className="text-[#39FF14] animate-[spin_8s_linear_infinite]" />
      <span className="font-display text-sm font-bold tracking-widest text-white">R.O.T.U.</span>
    </div>
    <div className="flex items-center gap-6">
      <nav className="hidden sm:flex items-center gap-8 text-[10px] tracking-[0.35em] uppercase text-zinc-500">
        <a href="#manifesto" className="hover:text-[#39FF14] transition-colors duration-200" data-testid="nav-manifesto">Ratty Rulez</a>
        <a href="#lineup" className="hover:text-[#39FF14] transition-colors duration-200" data-testid="nav-lineup">Lineup</a>
        <a href="#rsvp" className="hover:text-[#39FF14] transition-colors duration-200" data-testid="nav-rsvp">Entry</a>
      </nav>
      <button
        onClick={onToggle}
        data-testid="ally-toggle"
        aria-pressed={ally}
        className={`font-display text-[9px] sm:text-[10px] font-bold tracking-[0.25em] sm:tracking-[0.3em] uppercase px-2 py-1 sm:px-3 sm:py-1.5 transition-all duration-300 ${ally ? "ally-btn-on" : "ally-btn"}`}
      >
        {ally ? "FRUIT MODE" : "ALLY"}
      </button>
    </div>
  </header>
);

const Footer = () => (
  <footer className="relative z-10 border-t border-white/10 px-6 sm:px-12 lg:px-24 py-12 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="site-footer">
    <span className="font-display text-xs tracking-widest text-zinc-500">
      R.O.T.U. - NO CATS. NO LIGHTS. NO MERCY.
    </span>
    <span className="text-[10px] tracking-[0.35em] uppercase text-zinc-700">
      (c) 2026 THE COLONY // SECTOR 7
    </span>
  </footer>
);

function App() {
  const [ally, setAllyState] = useState(isAlly);
  const [confetti, setConfetti] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const fxUrl = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/music?slot=ally_fx`)
      .then((r) => {
        fxUrl.current = r.data.track ? `${BACKEND}${r.data.track.url}` : null;
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const kick = () => {
      startAmbience();
      window.removeEventListener("pointerdown", kick);
    };
    window.addEventListener("pointerdown", kick);
    return () => window.removeEventListener("pointerdown", kick);
  }, []);

  // owner-only: press E to toggle edit mode across editable boxes
  useEffect(() => {
    const onKey = (e) => {
      if (!e.target) return;
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      if (e.key.toLowerCase() === "e") {
        setEditMode((ed) => {
          window.dispatchEvent(new CustomEvent("rotu:edit", { detail: { editing: !ed } }));
          return !ed;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggleAlly = () => {
    const next = !ally;
    setAllyState(next);
    setAlly(next);
    if (next) {
      playSparkle();
      if (fxUrl.current) {
        const fx = new Audio(fxUrl.current);
        fx.play().then(() => { window.__rotu_fx = true; }).catch(() => {});
      }
      setConfetti(
        Array.from({ length: 90 }, (_, i) => ({
          id: i,
          left: Math.random() * 100,
          delay: Math.random() * 0.9,
          dur: 2.2 + Math.random() * 2,
          color: PRIDE[i % PRIDE.length],
          w: 5 + Math.random() * 6,
        }))
      );
      setTimeout(() => setConfetti(null), 5500);
    }
  };

  const [council, setCouncil] = useState(() => {
    try {
      return !!sessionStorage.getItem("rotu_council");
    } catch (e) {
      return false;
    }
  });

  if (window.location.pathname === "/guests") {
    return <GuestsPage />;
  }

  if (!council) {
    return <CouncilGate onUnlock={() => setCouncil(true)} />;
  }

  return (
    <div
      className={`relative min-h-screen bg-[#08080C] text-white ${ally ? "ally-scope" : ""}`}
      data-testid="app-root"
      data-theme={ally ? "ally" : "main"}
    >
      <LaserCanvas />
      <div className="fog fog-a" />
      <div className="fog fog-b" />
      {ally && <div className="ally-wash" data-testid="ally-wash" />}
      {ally && <FruitParty />}
      <div className="grain-overlay" />
      <div className="scanlines" />
      <div className="vignette-overlay" />
      <Header ally={ally} onToggle={toggleAlly} />
      <main>
        <Hero />
        <Marquee />
        <div id="manifesto">
          <Manifesto />
        </div>
        <div id="lineup">
          <Lineup />
        </div>
        <LocationReveal />
        <RsvpForm />
      </main>
      <Footer />
      <RatLayer />
      <MusicPlayer />
      <IntroOverlay />
      {editMode && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 z-[90] border border-[#FFE600] bg-[#08080C]/90 backdrop-blur-xl px-4 py-2 text-[10px] tracking-[0.3em] uppercase text-[#FFE600] pointer-events-none"
          data-testid="edit-mode-indicator"
        >
          Edit mode - press E to exit
        </div>
      )}
      {confetti && (
        <div data-testid="ally-confetti">
          {confetti.map((c) => (
            <span
              key={c.id}
              className="confetti-piece"
              style={{
                left: `${c.left}vw`,
                width: c.w,
                height: c.w * 1.6,
                backgroundColor: c.color,
                animationDuration: `${c.dur}s`,
                animationDelay: `${c.delay}s`,
              }}
            />
          ))}
        </div>
      )}
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

export default App;
